import { create } from 'zustand'
import type { Chat, ChatMessage } from '../types/chat.js'

/** messageId → set of userIds who read it */
type ReadReceipts = Record<string, Set<number>>

const PINNED_STORAGE_KEY = 'chat-interno:pinned'
const MAX_PINNED = 3

function loadPinnedIds(): string[] {
  try {
    const raw = localStorage.getItem(PINNED_STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function savePinnedIds(ids: string[]) {
  localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(ids))
}

interface ChatStore {
  chats: Chat[]
  activeChat: string | null
  messages: Record<string, ChatMessage[]>
  readReceipts: Record<string, ReadReceipts>
  pinnedChatIds: string[]
  isLoadingChats: boolean
  isLoadingMessages: boolean

  setChats: (chats: Chat[]) => void
  setActiveChat: (id: string | null) => void
  addChat: (chat: Chat) => void
  updateChat: (id: string, updates: Partial<Chat>) => void
  removeChat: (id: string) => void

  setMessages: (chatId: string, messages: ChatMessage[]) => void
  addMessage: (chatId: string, message: ChatMessage) => void
  updateMessage: (chatId: string, messageId: string, updates: Partial<ChatMessage>) => void
  removeMessage: (chatId: string, messageId: string) => void
  prependMessages: (chatId: string, messages: ChatMessage[]) => void

  addReadReceipt: (chatId: string, messageId: string, userId: number) => void
  getMessageReadCount: (chatId: string, messageId: string) => number

  togglePin: (chatId: string) => void
  isPinned: (chatId: string) => boolean

  setLoadingChats: (loading: boolean) => void
  setLoadingMessages: (loading: boolean) => void
}

export const useChatStore = create<ChatStore>((set, get) => ({
  chats: [],
  activeChat: null,
  messages: {},
  readReceipts: {},
  pinnedChatIds: loadPinnedIds(),
  isLoadingChats: false,
  isLoadingMessages: false,

  setChats: (chats) => set({ chats }),
  setActiveChat: (id) => set({ activeChat: id }),

  addChat: (chat) =>
    set((state) => ({
      chats: [chat, ...state.chats.filter((c) => c.id !== chat.id)],
    })),

  updateChat: (id, updates) =>
    set((state) => ({
      chats: state.chats.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    })),

  removeChat: (id) =>
    set((state) => ({
      chats: state.chats.filter((c) => c.id !== id),
      activeChat: state.activeChat === id ? null : state.activeChat,
    })),

  setMessages: (chatId, messages) =>
    set((state) => ({
      messages: { ...state.messages, [chatId]: messages },
    })),

  addMessage: (chatId, message) =>
    set((state) => {
      const existing = state.messages[chatId] ?? []
      if (existing.some((m) => m.id === message.id)) return state
      return {
        messages: { ...state.messages, [chatId]: [...existing, message] },
        chats: [
          ...state.chats.filter((c) => c.id === chatId),
          ...state.chats.filter((c) => c.id !== chatId),
        ],
      }
    }),

  updateMessage: (chatId, messageId, updates) =>
    set((state) => {
      const existing = state.messages[chatId]
      if (!existing) return state
      return {
        messages: {
          ...state.messages,
          [chatId]: existing.map((m) => (m.id === messageId ? { ...m, ...updates } : m)),
        },
      }
    }),

  removeMessage: (chatId, messageId) =>
    set((state) => {
      const existing = state.messages[chatId]
      if (!existing) return state
      return {
        messages: {
          ...state.messages,
          [chatId]: existing.filter((m) => m.id !== messageId),
        },
      }
    }),

  prependMessages: (chatId, messages) =>
    set((state) => {
      const existing = state.messages[chatId] ?? []
      return {
        messages: { ...state.messages, [chatId]: [...messages, ...existing] },
      }
    }),

  addReadReceipt: (chatId, messageId, userId) =>
    set((state) => {
      const chatReceipts = { ...state.readReceipts[chatId] }
      const existing = chatReceipts[messageId] ?? new Set<number>()
      if (existing.has(userId)) return state
      const updated = new Set(existing)
      updated.add(userId)
      chatReceipts[messageId] = updated
      return {
        readReceipts: { ...state.readReceipts, [chatId]: chatReceipts },
      }
    }),

  getMessageReadCount: (chatId, messageId) => {
    const chatReceipts = get().readReceipts[chatId]
    if (!chatReceipts) return 0
    return chatReceipts[messageId]?.size ?? 0
  },

  togglePin: (chatId) =>
    set((state) => {
      const isPinned = state.pinnedChatIds.includes(chatId)
      let newPinned: string[]
      if (isPinned) {
        newPinned = state.pinnedChatIds.filter((id) => id !== chatId)
      } else {
        if (state.pinnedChatIds.length >= MAX_PINNED) return state
        newPinned = [...state.pinnedChatIds, chatId]
      }
      savePinnedIds(newPinned)
      return { pinnedChatIds: newPinned }
    }),

  isPinned: (chatId) => get().pinnedChatIds.includes(chatId),

  setLoadingChats: (isLoadingChats) => set({ isLoadingChats }),
  setLoadingMessages: (isLoadingMessages) => set({ isLoadingMessages }),
}))
